import { inject, Injectable } from '@angular/core';
import {
  BackendService,
  BaseApiService,
  RequestFacadeModel,
  RequestModel,
  RequestType,
} from '@cf/core';
import { TranslateService } from '@tolgee/ngx';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { OrganizationsStore } from '../state/organizations.store';
import { IOrganizationUser, OrganizationsRolesEnum } from '../types';
import {
  IAddOrganizationsUser,
  IDemotionDetails,
  IHasSelectedStripeAccount,
  IOrganizationCalendarSettings,
  IOrganizationStripeSettings,
  IUpdateDefaultMeetingTool,
  IUpdateOrganizationCalendarSettings,
  IUpdateOrganizationStripeSettings,
  IUpdateOrganizationZoomSettings,
  Organization,
  OrganizationContacts,
  OrganizationInvitations,
  OrgZoomResponse,
} from '../types/organizations';

@Injectable({
  providedIn: 'root',
})
export class OrganizationsService extends BaseApiService {
  translateService = inject(TranslateService);
  #store = inject(OrganizationsStore);

  constructor(backendService: BackendService) {
    super(backendService, 'organizations');
  }

  get #selectedOrganizationId(): number {
    return Number(this.#store.selectedEntityId());
  }

  /**
   * @deprecated Use organizationsList instead
   */
  public fetchOrganizations(params?: Record<string, string>): Observable<Organization[]> {
    const requestUrl = this.getFullUrl('');
    const request = new RequestModel<null>({ url: requestUrl });

    if (params) {
      request.withQuery(params);
    }

    const requestFacade = new RequestFacadeModel<null>({
      requestType: RequestType.get,
      request,
    });

    return this.send<Organization[], null>(requestFacade);
  }

  public updateOrganization(
    dto: any,
    organizationId: number,
    successMessage?: { message: string; title: string },
  ): Observable<any> {
    const request: RequestModel<any> = new RequestModel<any>({
      url: this.getFullUrl(`${organizationId}`),
      requestBody: dto,
      successMessage: successMessage ?? {
        message: this.translateService.instant('organizations.messages.name_changed'),
        title: this.translateService.instant('organizations.messages.name_title'),
      },
      skipNotify: Boolean(dto.name),
    });
    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType: RequestType.patch,
      request,
    });
    return this.send<any, any>(requestFacade);
  }

  public addUser(users: IAddOrganizationsUser[]): Observable<any> {
    const request: RequestModel<any> = new RequestModel<any>({
      url: this.getFullUrl(`${this.#selectedOrganizationId}/invitations`),
      requestBody: { invitees: users },
      successMessage: {
        message: this.translateService.instant('organizations.messages.users_invited'),
        title: this.translateService.instant('organizations.messages.invitation_title'),
      },
    });
    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType: RequestType.post,
      request,
    });
    return this.send<any, any>(requestFacade);
  }

  public revokeUserInvitation(userId: number): Observable<any> {
    const request: RequestModel<any> = new RequestModel<any>({
      url: this.getFullUrl(`${this.#selectedOrganizationId}/invitations/${userId}/revoke`),
      successMessage: {
        message: this.translateService.instant('organizations.messages.invitation_revoked'),
        title: this.translateService.instant('organizations.messages.invitation_title'),
      },
    });
    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType: RequestType.post,
      request,
    });
    return this.send<any, any>(requestFacade);
  }

  public resendUserInvitation(userId: number): Observable<any> {
    const request: RequestModel<any> = new RequestModel<any>({
      url: this.getFullUrl(`${this.#selectedOrganizationId}/invitations/${userId}/resend`),
      successMessage: {
        message: this.translateService.instant('organizations.messages.invitation_resent'),
        title: this.translateService.instant('organizations.messages.invitation_title'),
      },
    });
    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType: RequestType.post,
      request,
    });
    return this.send<any, any>(requestFacade);
  }

  public deleteUser(userId: number): Observable<any> {
    const request: RequestModel<any> = new RequestModel<any>({
      url: this.getFullUrl(`${this.#selectedOrganizationId}/members/${userId}`),
      successMessage: {
        message: this.translateService.instant('organizations.messages.user_deleted'),
        title: this.translateService.instant('organizations.messages.user_title'),
      },
    });
    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType: RequestType.delete,
      request,
    });
    return this.send<any, any>(requestFacade);
  }

  public updateUserRole(
    userId: number,
    role: OrganizationsRolesEnum,
    organizationId: number,
  ): Observable<any> {
    const request: RequestModel<any> = new RequestModel<any>({
      url: this.getFullUrl(`${organizationId}/members/${userId}`),
      requestBody: { role },
      successMessage: {
        message: this.translateService.instant('organizations.messages.user_role_updated'),
        title: this.translateService.instant('organizations.messages.user_title'),
      },
    });
    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType: RequestType.patch,
      request,
    });
    return this.send<any, any>(requestFacade);
  }

  public getDemotionDetails(userId: number, organizationId: number): Observable<IDemotionDetails> {
    const request: RequestModel<any> = new RequestModel<any>({
      url: this.getFullUrl(`${organizationId}/members/${userId}/demotion-details`),
    });
    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType: RequestType.get,
      request,
    });
    return this.send<IDemotionDetails, any>(requestFacade);
  }

  public fetchInvitations(
    status?: OrganizationInvitations['status'],
  ): Observable<OrganizationInvitations[]> {
    let request: RequestModel<any> = new RequestModel<any>({
      url: 'organization-invitations/',
    });

    if (status) {
      request = request.withQuery({
        status,
      });
    }
    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType: RequestType.get,
      request,
    });
    return this.send<OrganizationInvitations[], any>(requestFacade);
  }

  public fetchInvitationsByToken(token: string): Observable<OrganizationInvitations> {
    const request: RequestModel<any> = new RequestModel<any>({
      url: `organization-invitations/${token}`,
    });

    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType: RequestType.get,
      request,
    });
    return this.send<OrganizationInvitations, any>(requestFacade);
  }

  public acceptInvitation(invitationId: number): Observable<any> {
    const request: RequestModel<any> = new RequestModel<any>({
      url: `organization-invitations/${invitationId}/accept/`,
      successMessage: {
        message: this.translateService.instant('organizations.messages.invitation_accepted'),
        title: this.translateService.instant('organizations.messages.invitation_title'),
      },
    });
    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType: RequestType.post,
      request,
    });
    return this.send<any, any>(requestFacade);
  }

  public acceptInvitationByToken(token: string): Observable<any> {
    const request: RequestModel<any> = new RequestModel<any>({
      url: `organization-invitations/${token}/accept/`,
      successMessage: {
        message: this.translateService.instant('organizations.messages.invitation_accepted'),
        title: this.translateService.instant('organizations.messages.invitation_title'),
      },
    });
    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType: RequestType.post,
      request,
    });
    return this.send<any, any>(requestFacade);
  }

  public declineInvitationByToken(token: string): Observable<any> {
    const request: RequestModel<any> = new RequestModel<any>({
      url: `organization-invitations/${token}/decline/`,
      successMessage: {
        message: this.translateService.instant('organizations.messages.invitation_declined'),
        title: this.translateService.instant('organizations.messages.invitation_title'),
      },
    });
    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType: RequestType.post,
      request,
    });
    return this.send<any, any>(requestFacade);
  }

  public declineInvitation(invitationId: number): Observable<any> {
    const request: RequestModel<any> = new RequestModel<any>({
      url: `organization-invitations/${invitationId}/decline/`,
      successMessage: {
        message: this.translateService.instant('organizations.messages.invitation_declined'),
        title: this.translateService.instant('organizations.messages.invitation_title'),
      },
    });
    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType: RequestType.post,
      request,
    });
    return this.send<any, any>(requestFacade);
  }

  public createOrganization(name: string): Observable<Organization> {
    const request = new RequestModel<{ name: string }>({
      url: this.getFullUrl(''),
      requestBody: { name },
      successMessage: {
        message: this.translateService.instant('organizations.messages.organization_created'),
        title: this.translateService.instant('organizations.messages.organization_title'),
      },
      skipNotify: true,
    });

    const requestFacade = new RequestFacadeModel<{ name: string }>({
      requestType: RequestType.post,
      request,
    });

    return this.send<Organization, { name: string }>(requestFacade).pipe(
      tap((organization) => {
        this.#store.addOrganization(organization);
        this.#store.selectEntity(organization.id);
      }),
    );
  }

  public leaveOrganization(organizationId: number): Observable<any> {
    const request = new RequestModel<null>({
      url: this.getFullUrl(`${organizationId}/leave`),
      successMessage: {
        message: this.translateService.instant('organizations.messages.organization_left'),
        title: this.translateService.instant('organizations.messages.organization_title'),
      },
    });

    const requestFacade = new RequestFacadeModel<null>({
      requestType: RequestType.post,
      request,
    });

    return this.send<any, null>(requestFacade).pipe(
      tap(() => {
        this.#store.removeOrganization(organizationId);
      }),
    );
  }

  public deleteOrganization(organizationId: number): Observable<any> {
    const request = new RequestModel<null>({
      url: this.getFullUrl(`${organizationId}`),
      successMessage: {
        message: this.translateService.instant('organizations.messages.organization_deleted'),
        title: this.translateService.instant('organizations.messages.organization_title'),
      },
    });

    const requestFacade = new RequestFacadeModel<null>({
      requestType: RequestType.delete,
      request,
    });

    return this.send<any, null>(requestFacade).pipe(
      tap(() => {
        this.#store.removeOrganization(organizationId);
      }),
    );
  }

  public fetchCalendars(byPassErrors = false): Observable<IOrganizationCalendarSettings[]> {
    const request: RequestModel<any> = new RequestModel<any>({
      url: this.getFullUrl(`calendar-settings`),
      skipNotify: byPassErrors,
    });
    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType: RequestType.get,
      request,
    });
    return this.send<IOrganizationCalendarSettings[], any>(requestFacade);
  }

  public updateCalendarSettings(
    dto: Partial<IUpdateOrganizationCalendarSettings>,
    organizationId: number,
  ): Observable<any> {
    const request: RequestModel<any> = new RequestModel<any>({
      url: this.getFullUrl(`calendar-settings/${organizationId}`),
      requestBody: dto,
      successMessage: {
        message: this.translateService.instant('organizations.messages.calendar_settings_updated'),
        title: this.translateService.instant('organizations.messages.calendar_settings_title'),
      },
    });
    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType: RequestType.patch,
      request,
    });
    return this.send<any, any>(requestFacade);
  }

  public fetchStripeSettings(): Observable<IOrganizationStripeSettings[]> {
    const request: RequestModel<any> = new RequestModel<any>({
      url: this.getFullUrl(`stripe`),
    });
    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType: RequestType.get,
      request,
    });
    return this.send<IOrganizationStripeSettings[], any>(requestFacade);
  }

  public updateStripeSettings(
    dto: Partial<IUpdateOrganizationStripeSettings>,
    organizationId: number,
  ): Observable<any> {
    const request: RequestModel<any> = new RequestModel<any>({
      url: this.getFullUrl(`stripe/${organizationId}`),
      requestBody: dto,
    });
    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType: RequestType.put,
      request,
    });
    return this.send<any, any>(requestFacade);
  }

  public hasSelectedStripeAccount(): Observable<IHasSelectedStripeAccount> {
    const request: RequestModel<any> = new RequestModel<any>({
      url: this.getFullUrl(`stripe/has-selected`),
    });
    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType: RequestType.get,
      request,
    });
    return this.send<IHasSelectedStripeAccount, any>(requestFacade);
  }

  public fetchZoomSettings(): Observable<any[]> {
    const request = new RequestModel({
      url: this.getFullUrl(`zoom`),
    });
    const requestFacade = new RequestFacadeModel({
      requestType: RequestType.get,
      request,
    });
    return this.send(requestFacade);
  }

  public fetchZoomSettingsByOrganizationId(organizationId: number): Observable<OrgZoomResponse> {
    const request = new RequestModel({
      url: this.getFullUrl(`zoom/${organizationId}`),
    });
    const requestFacade = new RequestFacadeModel({
      requestType: RequestType.get,
      request,
    });
    return this.send(requestFacade);
  }

  public updateZoomSettings(dto: Partial<IUpdateOrganizationZoomSettings>, organizationId: number) {
    const request = new RequestModel({
      url: this.getFullUrl(`zoom/${organizationId}`),
      requestBody: dto,
    });
    const requestFacade = new RequestFacadeModel({
      requestType: RequestType.put,
      request,
    });
    return this.send(requestFacade);
  }

  public updateDefaultMeetingTool(dto: Partial<IUpdateDefaultMeetingTool>, organizationId: number) {
    const request = new RequestModel({
      url: this.getFullUrl(`${organizationId}/default-meeting-tool-source`),
      requestBody: dto,
    });
    const requestFacade = new RequestFacadeModel({
      requestType: RequestType.put,
      request,
    });
    return this.send(requestFacade);
  }

  public fetchOrganizationContacts(search?: string): Observable<OrganizationContacts[]> {
    const request = new RequestModel({
      url: this.getFullUrl(`${this.#selectedOrganizationId}/contacts`),
    }).withQuery(search ? { search, limit: '50' } : { limit: '50' });
    const requestFacade = new RequestFacadeModel({
      requestType: RequestType.get,
      request,
    });
    return this.send<{ results: OrganizationContacts[] }, null>(requestFacade).pipe(
      map((res) => res.results),
    );
  }

  /**
   * Fetch organization users with pagination, filtering and sorting support
   */
  public fetchOrgUsers(
    organizationId: number,
    params: {
      limit?: string;
      offset?: string;
      search?: string;
      role?: string;
      ordering?: string;
    } = {},
  ): Observable<{ results: IOrganizationUser[]; count: number }> {
    const requestUrl = this.getFullUrl(`${organizationId}/users`);
    const request = new RequestModel<null>({ url: requestUrl }).withQuery(params);

    const requestFacade = new RequestFacadeModel<null>({
      requestType: RequestType.get,
      request,
    });

    return this.send<{ results: IOrganizationUser[]; count: number }, null>(requestFacade);
  }

  /**
   * Fetch organizations list with pagination, filtering and sorting support
   */
  public organizationsList(
    params: {
      limit?: string;
      offset?: string;
      search?: string;
      is_owner?: string;
      ordering?: string;
    } = {},
  ): Observable<{ results: Organization[]; count: number }> {
    const requestUrl = this.getFullUrl(``);
    const request = new RequestModel<null>({ url: requestUrl }).withQuery(params);

    const requestFacade = new RequestFacadeModel<null>({
      requestType: RequestType.get,
      request,
    });

    return this.send<{ results: Organization[]; count: number }, null>(requestFacade);
  }
}
